import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader, Text, Paper, Stack, Button } from '@mantine/core';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useGoogleAuthCallback } from '@/hooks/api-hooks';

export default function GoogleCallbackPage(): JSX.Element {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const callbackMutation = useGoogleAuthCallback();
    const processedRef = useRef(false);
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const closeSelf = () => {
        try {
            window.open('', '_self', '');
            window.close();
        } catch {
            // fallback
        }
        try {
            window.close();
        } catch {
            // fallback
        }
    };

    useEffect(() => {
        if (processedRef.current) return;
        const code = searchParams.get('code');
        if (!code) {
            setStatus('error');
            setErrorMessage(t('invalid_auth_code'));
            return;
        }

        processedRef.current = true;
        const redirectUri = `${window.location.origin}/google-callback`;

        callbackMutation.mutate({ code, redirectUri }, {
            onSuccess: () => {
                setStatus('success');

                // 1. BroadcastChannel (notifies parent immediately without cross-window restrictions)
                try {
                    const channel = new BroadcastChannel('google_oauth_channel');
                    channel.postMessage({ type: 'GOOGLE_AUTH_SUCCESS' });
                    channel.close();
                } catch (e) {
                    console.error('BroadcastChannel error:', e);
                }

                // 2. Storage event
                try {
                    localStorage.setItem('google_auth_event', Date.now().toString());
                } catch (e) {
                    console.error('Storage error:', e);
                }

                // 3. PostMessage to opener
                try {
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage({ type: 'GOOGLE_AUTH_SUCCESS' }, '*');
                    }
                } catch (e) {
                    console.error('Opener postMessage error:', e);
                }

                // 4. Close immediately
                closeSelf();

                // 5. Retry closing after 500ms and 1200ms
                setTimeout(closeSelf, 500);
                setTimeout(closeSelf, 1200);
            },
            onError: (err: any) => {
                setStatus('error');
                setErrorMessage(err?.message || null);
                try {
                    if (window.opener && !window.opener.closed) {
                        window.opener.postMessage({ type: 'GOOGLE_AUTH_ERROR' }, '*');
                    }
                } catch (e) {
                    console.error('Opener error:', e);
                }
            }
        });
    }, [searchParams, callbackMutation, t]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4">
            <Paper withBorder className="p-8 rounded-2xl shadow-sm text-center max-w-sm w-full bg-white">
                {status === 'loading' && (
                    <Stack className="items-center gap-3">
                        <Loader size="md" color="blue" />
                        <Text className="font-bold text-base text-gray-800">
                            {t('connecting_google_account')}
                        </Text>
                        <Text className="text-xs text-gray-500">
                            {t('please_wait_while_connecting')}
                        </Text>
                    </Stack>
                )}

                {status === 'success' && (
                    <Stack className="items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                            <IconCircleCheck size={36} stroke={1.5} />
                        </div>
                        <div className="space-y-1">
                            <Text className="font-bold text-lg text-gray-900">
                                {t('google_account_connected')}
                            </Text>
                            <Text className="text-xs text-gray-500">
                                {t('google_auth_success_hint')}
                            </Text>
                        </div>
                        <Button color="dark" fullWidth onClick={closeSelf} radius="md" className="mt-2">
                            {t('close_window')}
                        </Button>
                    </Stack>
                )}

                {status === 'error' && (
                    <Stack className="items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center text-rose-600">
                            <IconAlertCircle size={36} stroke={1.5} />
                        </div>
                        <div className="space-y-1">
                            <Text className="font-bold text-lg text-gray-900">
                                {t('google_account_connection_failed')}
                            </Text>
                            <Text className="text-xs text-gray-500">
                                {errorMessage || t('google_auth_failed_hint')}
                            </Text>
                        </div>
                        <Button variant="default" fullWidth onClick={closeSelf} radius="md" className="mt-2">
                            {t('close_window')}
                        </Button>
                    </Stack>
                )}
            </Paper>
        </div>
    );
}
